import type { FormRAssignmentLine } from '../form-r/form-r-sections.ts';
import {
  formulaChoiceQuestionID,
  numberQuestionID,
  productSolidsQuestionID,
  questionnaireFindChemicalSpecificQuestions,
  questionsReplaceCasPlaceholders,
  questionsRequiredFromReleaseProfiles,
} from '../questionnaires.ts';
import { productUsageGalVar, productUsageLbVar, rainfallVar } from '../formulas/formula-utils.ts';
import { potentialBasisPeriodHours } from './potential-basis-period.ts';
import { type EmissionsPeriodDetails, periodPotential } from './emissions-dates.ts';
import type {
  ChemicalBrief,
  QuestionBriefNumber,
  QuestionBriefProductSolids,
  QuestionBriefProductUsage,
  QuestionBriefReleaseProfileFormulaChoice,
  ReleaseProfileProcessChemical,
  ReleaseProfileAnswerValues,
  Warning,
} from '../../types/tri.ts';
import type {
  FormulaOutputType,
  ReleaseProfileFormulaChoiceID,
  ReleaseProfileQuestion,
  ReleaseProfilesAndVariables,
} from '../../types/release-profiles.ts';
import type { EmissionUnitTypeWithConfig, EmissionUnitWithConfig } from '../../types/emissions.ts';
import { releaseProfileAnnotatableVariableNames, releaseProfilesIterateChemicals } from '../release-profiles.ts';
import type {
  CasOrCategory,
  EmissionsAnswer,
  EmissionsPeriod,
  EmissionsUnit,
  EmissionsUnitID,
  EmissionsUnitTypeID,
  FacilityID,
  ProductID,
  ReleaseProfileID,
  ReleaseProfileQuestionID,
  ReleaseProfileVariableID,
  UsageTypeID,
  UsageUnits,
} from '../../types/db.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';
import type { QuestionnaireFormulaChoicesContainer } from '../../types/questionnaires.ts';
import { type CalculationLine, chemicalCalculationsExecute } from '../releases-calculate.ts';
import { jsonStableStringify } from '../json-stable-stringify.ts';

export type EmissionsQuestionBrief =
  | QuestionBriefNumber
  | QuestionBriefProductSolids
  | QuestionBriefProductUsage
  | QuestionBriefReleaseProfileFormulaChoice;

// ReleaseProfileProcessWithChildren minimized for just what the emissions tool needs:
export interface EmissionsProcessAndProfiles {
  formRLine: FormRAssignmentLine | null;
  processName: string;
  facilityID: FacilityID;
  profiles: {
    releaseProfileID: ReleaseProfileID;
    releaseProfileName: string;
    thresholdOrRelease: 'release' | 'threshold' | 'both';
    thresholdUsageTypeID: UsageTypeID | null;
    products: {
      productID: ProductID;
      productName: string;
      specificGravity: number | null;
      usageUnits: UsageUnits;
      solidsPercentageByWeight: number | null;
      solidsPercentageByVolume: number | null;
      archivedAt: ISO8601DateOnly | null;
      chemicalsExcluded: CasOrCategory[];
      chemicals: ReleaseProfileProcessChemical[];
    }[];
  }[];
}

export function emissionsUnitRelevantProfilesAndChemicals(
  unitType: EmissionUnitTypeWithConfig,
  unit: EmissionUnitWithConfig,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>,
): { chemicals: Map<CasOrCategory, string>; process: EmissionsProcessAndProfiles } {
  const chemicals = new Map<CasOrCategory, string>([
    ['PM', 'PM'],
    ['PM10', 'PM10'],
    ['PM2.5', 'PM2.5'],
    ['PM Filterable', 'PM Filterable'],
    ['PM10 Filterable', 'PM10 Filterable'],
    ['PM2.5 Filterable', 'PM2.5 Filterable'],
    ['PM Condensable', 'PM Condensable'],
    ['VOC', 'VOC'],
    ['CO2e', 'CO2e'],
    ['F', 'Fluorides'],
    // These categories will not get mapped automatically, so anything we directly output will have been provided
    // by the user explicitly on a chemical profile or product:
    ['NOx', 'NOx'],
    ['SOx', 'SOx'],
  ] as [CasOrCategory, string][]);

  for (const product of unit.products) {
    for (const { casOrCategoryOriginal, chemicalName } of product.chemicals) {
      chemicals.set(casOrCategoryOriginal, chemicalName);
    }
  }

  const uniqueReleaseProfiles = new Map<ReleaseProfileID, string>();
  for (const { releaseProfileID, name: releaseProfileName } of unitType.releaseProfiles) {
    uniqueReleaseProfiles.set(releaseProfileID, releaseProfileName);
  }
  for (const { releaseProfileID, name: releaseProfileName } of unit.limitsWithReleaseProfiles) {
    uniqueReleaseProfiles.set(releaseProfileID, releaseProfileName);
  }

  const profiles: EmissionsProcessAndProfiles['profiles'] = [];
  for (const [releaseProfileID, releaseProfileName] of uniqueReleaseProfiles.entries()) {
    profiles.push({
      releaseProfileID,
      releaseProfileName,
      thresholdOrRelease: 'both',
      thresholdUsageTypeID: null,
      products: unit.products.map((product) => {
        return {
          productID: product.productID,
          productName: product.name,
          specificGravity: product.specificGravity,
          usageUnits: product.usageUnits,
          solidsPercentageByWeight: product.solidsPercentageByWeight,
          solidsPercentageByVolume: product.solidsPercentageByVolume,
          archivedAt: product.archivedAt,
          chemicalsExcluded: [],
          chemicals: product.chemicals,
        };
      }),
    });

    const profile = releaseProfilesAndVariables.releaseProfiles.find((p) => p.releaseProfileID === releaseProfileID);
    if (profile) {
      const profileChemicals = releaseProfilesIterateChemicals(
        releaseProfilesAndVariables,
        // Usage mixes, if used, will need to be based on the products and usage assigned to the unit. Therefore, we
        // will have already captured the relevant product chemicals in emissionsUnitToQuestions, and we don't need
        // the usage mix results here:
        {},
        profile.releaseProfileID,
      );

      for (const casOrCategory of profileChemicals) {
        if (!chemicals.has(casOrCategory)) {
          chemicals.set(casOrCategory, chemicalsByCasOrCategory[casOrCategory]?.name ?? casOrCategory);
        }
      }
    }
  }

  const process: EmissionsProcessAndProfiles = {
    formRLine: null,
    processName: `${unit.name} - ${unitType.name}`,
    facilityID: unit.facilityID,
    profiles,
  };

  return { chemicals, process };
}

export interface EmissionsQuestionAnnotated<Question extends EmissionsQuestionBrief = EmissionsQuestionBrief> {
  question: Question;
  unitTypeConfig:
    | EmissionUnitTypeWithConfig['variableConfigs'][number]
    | EmissionUnitTypeWithConfig['predefinedVariableConfigs'][number]
    | EmissionUnitTypeWithConfig['formulaChoiceConfigs'][number]
    | null;
  unitConfig:
    | EmissionUnitWithConfig['variableConfigs'][number]
    | EmissionUnitWithConfig['formulaChoiceConfigs'][number]
    | null;
  source:
    | 'unitType'
    | 'unit'
    | 'product'
    | 'facilityIntervalAnswers'
    | 'unitTypeIntervalAnswers'
    | 'unitIntervalAnswers';
  isVisible: (periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer) => boolean;
}

function unitToAcceptableOutputTypes(
  unit: Pick<EmissionUnitWithConfig, 'trackingMode' | 'limitsWithReleaseProfiles'>,
): Set<FormulaOutputType> {
  const acceptableOutputType = new Set<FormulaOutputType>();
  if (unit.trackingMode === 'trackingActuals') {
    acceptableOutputType.add('actual');
    acceptableOutputType.add('potential');
  } else {
    acceptableOutputType.add('potential');
  }

  if (unit.limitsWithReleaseProfiles.length > 0) {
    acceptableOutputType.add('complianceNumerator');
    acceptableOutputType.add('complianceDenominator');
  }

  return acceptableOutputType;
}

function unitPeriodToAcceptableOutputTypes(
  unit: Pick<EmissionUnitWithConfig, 'trackingMode' | 'limitsWithReleaseProfiles'>,
  period: EmissionsPeriod | 'potential',
): Set<FormulaOutputType> {
  if (period === 'potential') {
    return new Set(['potential']);
  }

  const acceptableOutputTypes = new Set<FormulaOutputType>();
  if (unit.trackingMode === 'trackingActuals') {
    acceptableOutputTypes.add('actual');
  } else if (unit.trackingMode === 'rollPteIntoActuals') {
    acceptableOutputTypes.add('potential');
  }

  if (unit.limitsWithReleaseProfiles.length > 0) {
    acceptableOutputTypes.add('complianceNumerator');
    acceptableOutputTypes.add('complianceDenominator');
  }

  return acceptableOutputTypes;
}

export function emissionsUnitToQuestions(
  unitType: EmissionUnitTypeWithConfig,
  unit: EmissionUnitWithConfig,
  chemicals: Map<CasOrCategory, string>,
  process: EmissionsProcessAndProfiles,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>,
): EmissionsQuestionAnnotated[] {
  const acceptableOutputType = unitToAcceptableOutputTypes(unit);
  const questionsByFormulaChoices = new Map<string, EmissionsQuestionBrief[]>();
  const questionIsVisibleWithFormulaChoices = new Map<`${string}-${ReleaseProfileQuestionID}`, boolean>();
  const questionsGet = (
    periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer,
  ): EmissionsQuestionBrief[] => {
    const key = jsonStableStringify(periodSpecificFormulaChoices);
    if (questionsByFormulaChoices.has(key)) {
      return questionsByFormulaChoices.get(key)!;
    }

    const questions = questionsRequiredFromReleaseProfiles(
      { type: 'emissions', acceptableOutputType },
      Array.from(chemicals.keys()),
      periodSpecificFormulaChoices,
      [process],
      {
        releaseProfiles: releaseProfilesAndVariables.releaseProfiles,
        questions: releaseProfilesAndVariables.questions,
        mixCalculations: [], // Mix calculations are unused and likely to be removed, so we can skip those.
        wasteStreams: [], // Waste streams are too wrapped up in TRI to be sensible in emissions units.
      },
      releaseProfileAnnotatableVariableNames(releaseProfilesAndVariables),
    ).filter(
      (q) =>
        q.type !== 'wasteStreamAttestedZero' &&
        q.type !== 'wasteStreamAllocation' &&
        q.type !== 'wasteStreamDirectInput',
    );
    questionsByFormulaChoices.set(key, questions);
    questionsReplaceCasPlaceholders(
      questions,
      (casOrCategory) => chemicalsByCasOrCategory[casOrCategory]?.name ?? null,
    );
    return questions;
  };

  const releaseProfileQuestionByVariableID = new Map<ReleaseProfileVariableID, ReleaseProfileQuestion>(
    releaseProfilesAndVariables.questions.map((q) => [q.releaseProfileVariableID, q]),
  );

  const unitTypeQuestionConfigsByQuestionID = new Map<
    ReleaseProfileQuestionID | string,
    | EmissionUnitTypeWithConfig['variableConfigs'][number]
    | EmissionUnitTypeWithConfig['predefinedVariableConfigs'][number]
    | EmissionUnitTypeWithConfig['formulaChoiceConfigs'][number]
  >();

  for (const unitTypeVariableConfig of unitType.variableConfigs) {
    const question = releaseProfileQuestionByVariableID.get(unitTypeVariableConfig.releaseProfileVariableID);
    if (!question) {
      continue;
    }

    const baseQuestionID = question.definition.questionID;
    const questionID = numberQuestionID(baseQuestionID, unitTypeVariableConfig.casOrCategory);
    unitTypeQuestionConfigsByQuestionID.set(questionID, unitTypeVariableConfig);

    if (!unitTypeVariableConfig.casOrCategory && question.definition.isChemicalSpecific) {
      for (const casOrCategory of chemicals.keys()) {
        const questionID = numberQuestionID(baseQuestionID, casOrCategory);
        unitTypeQuestionConfigsByQuestionID.set(questionID, unitTypeVariableConfig);
      }
    }
  }

  for (const unitTypePredefinedVariableConfig of unitType.predefinedVariableConfigs) {
    unitTypeQuestionConfigsByQuestionID.set(
      unitTypePredefinedVariableConfig.predefinedVariableName,
      unitTypePredefinedVariableConfig,
    );
  }

  for (const unitTypeFormulaChoiceConfig of unitType.formulaChoiceConfigs) {
    const questionID = formulaChoiceQuestionID(unitTypeFormulaChoiceConfig.choiceListID);
    unitTypeQuestionConfigsByQuestionID.set(questionID, unitTypeFormulaChoiceConfig);
  }

  const unitQuestionConfigsByQuestionID = new Map<
    ReleaseProfileQuestionID,
    EmissionUnitWithConfig['variableConfigs'][number] | EmissionUnitWithConfig['formulaChoiceConfigs'][number]
  >();

  for (const unitVariableConfig of unit.variableConfigs) {
    const question = releaseProfileQuestionByVariableID.get(unitVariableConfig.releaseProfileVariableID);
    if (!question) {
      continue;
    }

    const baseQuestionID = question.definition.questionID;
    const questionID = numberQuestionID(baseQuestionID, unitVariableConfig.casOrCategory);
    unitQuestionConfigsByQuestionID.set(questionID, unitVariableConfig);

    if (!unitVariableConfig.casOrCategory && question.definition.isChemicalSpecific) {
      for (const casOrCategory of chemicals.keys()) {
        const questionID = numberQuestionID(baseQuestionID, casOrCategory);
        unitQuestionConfigsByQuestionID.set(questionID, unitVariableConfig);
      }
    }
  }

  for (const unitFormulaChoiceConfig of unit.formulaChoiceConfigs) {
    const questionID = formulaChoiceQuestionID(unitFormulaChoiceConfig.choiceListID);
    unitQuestionConfigsByQuestionID.set(questionID, unitFormulaChoiceConfig);
  }

  return questionsGet(
    // Passing an empty 'periodSpecificFormulaChoices' will cause us to generate all possible questions:
    {},
  ).map((question): EmissionsQuestionAnnotated => {
    let unitTypeConfig;
    if (question.type === 'productUsage') {
      if (question.units === 'pounds') {
        unitTypeConfig =
          unitTypeQuestionConfigsByQuestionID.get(productUsageLbVar) ??
          unitTypeQuestionConfigsByQuestionID.get(productUsageGalVar) ??
          null;
      } else {
        unitTypeConfig =
          unitTypeQuestionConfigsByQuestionID.get(productUsageGalVar) ??
          unitTypeQuestionConfigsByQuestionID.get(productUsageLbVar) ??
          null;
      }
    } else if (question.questionID === 'question_rainfall') {
      unitTypeConfig = unitTypeQuestionConfigsByQuestionID.get(rainfallVar) ?? null;
    } else {
      unitTypeConfig = unitTypeQuestionConfigsByQuestionID.get(question.questionID) ?? null;
    }

    let source: EmissionsQuestionAnnotated['source'];
    if (question.type === 'productSolids') {
      source = 'product';
    } else if (unitTypeConfig?.frequency === 'once') {
      source = 'unitType';
    } else if (unitTypeConfig?.frequency === 'perUnit') {
      source = 'unit';
    } else if (unitTypeConfig?.frequency === 'perFacilityPerInterval') {
      source = 'facilityIntervalAnswers';
    } else if (unitTypeConfig?.frequency === 'perUnitTypePerInterval') {
      source = 'unitTypeIntervalAnswers';
    } else if (unitTypeConfig?.frequency === 'perInterval' || !unitTypeConfig?.frequency) {
      source = 'unitIntervalAnswers';
    } else {
      const unexpected: never = unitTypeConfig?.frequency;
      console.error('emissionsUnitToQuestions: Unexpected config frequency:', unexpected);
      source = 'unitIntervalAnswers';
    }

    return {
      question,
      unitTypeConfig,
      unitConfig: unitQuestionConfigsByQuestionID.get(question.questionID) ?? null,
      source,
      isVisible: (periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer) => {
        const key = `${jsonStableStringify(periodSpecificFormulaChoices)}-${question.questionID}` as const;
        if (questionIsVisibleWithFormulaChoices.has(key)) {
          return questionIsVisibleWithFormulaChoices.get(key)!;
        }

        const isVisible = !!questionsGet(periodSpecificFormulaChoices).find(
          (q) => q.questionID === question.questionID,
        );
        questionIsVisibleWithFormulaChoices.set(key, isVisible);
        return isVisible;
      },
    };
  });
}

export type EmissionsAnswerByPeriodAndQuestion = Partial<
  Record<`${EmissionsPeriod | 'potential'}-${ReleaseProfileQuestionID}`, EmissionsAnswer['answer'] | ''>
>;

/**
 * Which column of the questionnaire holds the answers one output type is computed from.
 *
 * Potential emissions describe the unit as it is configured rather than what a calendar period recorded, so their
 * inputs are answered once, in the PTE column. Everything else is answered against the period it measures — including
 * the compliance metrics of a unit that rolls its PTE into its actuals, whose emissions come from the PTE column but
 * whose metrics still measure that period's own throughput.
 */
export function emissionsAnswersPeriodForOutput(
  period: EmissionsPeriod | 'potential',
  outputType: FormulaOutputType,
): EmissionsPeriod | 'potential' {
  return outputType === 'potential' ? 'potential' : period;
}

/**
 * Whether a period asks a question at all — i.e. whether the questionnaire has a cell for it.
 *
 * This is the mirror of `emissionsAnswersPeriodForOutput`: a question is asked in the column the outputs that want it
 * are computed from. A question wanted only by a potential output is therefore asked once, in the PTE column, and one
 * wanted by an actual or compliance output is asked in each period. A question wanted by both is asked in both, and
 * the two answers are independent — that is the point, since they describe different things.
 *
 * Questions no output of this unit wants never reach here: `emissionsUnitToQuestions` has already dropped them.
 */
export function emissionsQuestionAppliesToPeriod(
  period: EmissionsPeriod | 'potential',
  question: Pick<EmissionsQuestionBrief, 'formulaOutputTypes'>,
): boolean {
  if (period === 'potential') {
    return question.formulaOutputTypes.includes('potential');
  }

  return question.formulaOutputTypes.some((outputType) => outputType !== 'potential');
}

/**
 * The columns a period's numbers were answered in, which is normally just the period itself. A unit that rolls its PTE
 * into its actuals computes the period's emissions from the PTE column, so anything asking whether a period has been
 * answered for — completion stats above all — has to count that column too, or a period that asks nothing of its own
 * reads as complete however empty the column it was computed from.
 */
export function emissionsPeriodsAnswering(
  trackingMode: EmissionsUnit['trackingMode'],
  period: EmissionsPeriodDetails,
): EmissionsPeriodDetails[] {
  if (trackingMode !== 'rollPteIntoActuals' || period.type === 'potential') {
    return [period];
  }

  return [period, periodPotential(period.end)];
}

export interface EmissionsUnitCompletionStats {
  emissionsUnitID: EmissionsUnitID;
  totalCells: number;
  answeredCells: number;
  totalDuePeriods: number;
  completePeriods: number;
  duePeriodsWithAnyAnswer: number;
}

export interface EmissionsUnitCompletionStatFromList extends EmissionsUnitCompletionStats {
  unitName: string;
  emissionsUnitTypeID: EmissionsUnitTypeID;
  trackingFrequency: EmissionsUnit['trackingFrequency'];
}

/**
 * Compute completion stats for a single emissions unit across the periods it is handed, counting exactly the cells
 * the questionnaire grid shows for each of them (see `emissionsQuestionAppliesToPeriod`). Callers that want the PTE
 * column counted — where a unit that rolls its PTE into its actuals or reports only its PTE keeps most of its cells —
 * pass the pseudo-period from `periodPotential` along with the real ones, or ask `emissionsPeriodsAnswering` which
 * columns one period was answered in.
 *
 * Counts only "interval" questions (unit / facility / unit-type interval answers and product
 * usage) — config-answered questions (`unit` / `unitType` source) are excluded because they
 * are stored on the unit/unit-type config and not in the period grid.
 *
 * Pure function — all inputs are pre-loaded; safe to use server or client side.
 */
export function emissionsUnitCompletionStats(
  unit: EmissionUnitWithConfig,
  unitType: EmissionUnitTypeWithConfig,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>,
  answersFromUnit: EmissionsAnswerByPeriodAndQuestion,
  answersFromFacility: EmissionsAnswerByPeriodAndQuestion,
  answersFromUnitType: EmissionsAnswerByPeriodAndQuestion,
  productAnswers: Partial<Record<`${ProductID}-${QuestionBriefProductSolids['subType']}`, number | null>>,
  today: ISO8601DateOnly | null, // Pass null to evaluate every period.
  periodsForUnit: Iterable<{ period: EmissionsPeriod | 'potential'; start: ISO8601DateOnly; end: ISO8601DateOnly }>,
): EmissionsUnitCompletionStats {
  const { chemicals, process } = emissionsUnitRelevantProfilesAndChemicals(
    unitType,
    unit,
    releaseProfilesAndVariables,
    chemicalsByCasOrCategory,
  );
  const questions = emissionsUnitToQuestions(
    unitType,
    unit,
    chemicals,
    process,
    releaseProfilesAndVariables,
    chemicalsByCasOrCategory,
  );

  let totalCells = 0;
  let answeredCells = 0;
  let completePeriods = 0;
  let totalDuePeriods = 0;
  let duePeriodsWithAnyAnswer = 0;

  // Pre-build set of product IDs and their archive dates for archived-product filtering.
  const productArchivedAt = new Map<ProductID, ISO8601DateOnly | null>();
  for (const product of unit.products) {
    productArchivedAt.set(product.productID, product.archivedAt);
  }

  for (const period of periodsForUnit) {
    // The PTE column is answered from the unit's current configuration rather than from what a calendar period
    // recorded, so it is never "not yet due" and never one of the periods a facility can fall behind on.
    const isPotential = period.period === 'potential';
    if (today && !isPotential && period.end >= today) continue;

    // Pass 1: derive formula choices for the period from whatever answers we have so we can
    // ask each question whether it's visible.
    const periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer = {};
    for (const annotated of questions) {
      if (annotated.question.type !== 'releaseProfileFormulaChoice') continue;
      const key = `${period.period}-${annotated.question.questionID}` as const;
      let value: EmissionsAnswer['answer'] | '' | undefined;
      const source = annotated.source;
      if (source === 'unitIntervalAnswers') value = answersFromUnit[key];
      else if (source === 'facilityIntervalAnswers') value = answersFromFacility[key];
      else if (source === 'unitTypeIntervalAnswers') value = answersFromUnitType[key];
      else if (source === 'unit') value = annotated.unitConfig?.answer ?? undefined;
      else if (source === 'unitType')
        value =
          annotated.unitTypeConfig && 'answer' in annotated.unitTypeConfig
            ? (annotated.unitTypeConfig.answer ?? undefined)
            : undefined;
      else if (source === 'product') {
        // Formula choices never come from product-source questions — leave value undefined.
      } else {
        const _exhaustive: never = source;
        void _exhaustive;
      }
      if (typeof value === 'string' && value.startsWith('formula_choice_')) {
        periodSpecificFormulaChoices[annotated.question.choiceListID] = value as ReleaseProfileFormulaChoiceID;
      }
    }

    // Pass 2: count visible interval questions and how many are answered.
    let periodTotal = 0;
    let periodAnswered = 0;
    for (const annotated of questions) {
      // Skip questions whose answers live on the config (unit / unitType) — those are not
      // part of the period grid and aren't "cells" the user fills out per period.
      const source = annotated.source;
      if (source === 'unit' || source === 'unitType') continue;
      if (!annotated.isVisible(periodSpecificFormulaChoices)) continue;

      // Skip product-related questions if the product was archived before this period.
      if (annotated.question.type === 'productUsage' || annotated.question.type === 'productSolids') {
        const archivedAt = productArchivedAt.get(annotated.question.productID);
        if (archivedAt && archivedAt <= period.start) continue;
      }

      // Skip questions this period doesn't ask — the ones answered in another of the unit's columns:
      if (!emissionsQuestionAppliesToPeriod(period.period, annotated.question)) continue;

      let value: EmissionsAnswer['answer'] | '' | number | null | undefined;
      if (source === 'product') {
        if (annotated.question.type === 'productSolids') {
          value = productAnswers[`${annotated.question.productID}-${annotated.question.subType}`];
        }
      } else if (source === 'unitIntervalAnswers') {
        value = answersFromUnit[`${period.period}-${annotated.question.questionID}`];
      } else if (source === 'facilityIntervalAnswers') {
        value = answersFromFacility[`${period.period}-${annotated.question.questionID}`];
      } else if (source === 'unitTypeIntervalAnswers') {
        value = answersFromUnitType[`${period.period}-${annotated.question.questionID}`];
      } else {
        const _exhaustive: never = source;
        void _exhaustive;
      }

      periodTotal++;
      if (value !== undefined && value !== null && value !== '') {
        periodAnswered++;
      }
    }

    totalCells += periodTotal;
    answeredCells += periodAnswered;

    // A period a unit asks nothing of can neither fall behind nor be completed — a unit rolling its PTE into its
    // actuals asks nothing of any of them — so only periods with cells of their own are counted as due.
    if (isPotential || periodTotal === 0) continue;
    totalDuePeriods++;
    if (periodAnswered === periodTotal) completePeriods++;
    if (periodAnswered > 0) duePeriodsWithAnyAnswer++;
  }

  return {
    emissionsUnitID: unit.emissionsUnitID,
    totalCells,
    answeredCells,
    totalDuePeriods,
    completePeriods,
    duePeriodsWithAnyAnswer,
  };
}

export function calculateEmissions(
  trackingMode: EmissionsUnit['trackingMode'],
  limitsWithReleaseProfiles: EmissionUnitWithConfig['limitsWithReleaseProfiles'],
  profilesAndVariables: ReleaseProfilesAndVariables,
  chemicals: Set<CasOrCategory>,
  questions: EmissionsQuestionAnnotated[],
  process: EmissionsProcessAndProfiles,
  periodOrig: EmissionsPeriod | 'potential',
  answersMap: EmissionsAnswerByPeriodAndQuestion,
  periodStart: ISO8601DateOnly,
  periodEnd: ISO8601DateOnly,
): { calculationLines: CalculationLine[]; warnings: Warning[] } {
  // Build the set of product IDs archived before this period so we can skip their usage and exclude
  // them from the process structure fed into calculation. Historic periods before the archival date
  // will still include the product's contributions.
  const archivedProductIds = new Set<ProductID>();
  for (const profile of process.profiles) {
    for (const product of profile.products) {
      if (product.archivedAt && product.archivedAt <= periodStart) {
        archivedProductIds.add(product.productID);
      }
    }
  }

  const periodHours =
    (new Date(`${periodEnd}T00:00:00Z`).getTime() - new Date(`${periodStart}T00:00:00Z`).getTime()) / 3_600_000 + 24;

  /**
   * Everything one column of the questionnaire contributes to a calculation. A period reads its own column for
   * everything it records, but a unit that rolls its PTE into its actuals computes its potential output from the PTE
   * column whichever period it is reporting, so one period can read from two columns.
   */
  function inputsFromColumn(column: EmissionsPeriod | 'potential') {
    const releaseProfileAnswers: ReleaseProfileAnswerValues = {};
    const releaseProfileChemicalNaAnswers: Partial<Record<ReleaseProfileQuestionID, boolean>> = {};
    const releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer = {};
    const productUsage = new Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>();

    let usingProductSolids = false;
    for (const { question } of questions) {
      const answer = answersMap[`${column}-${question.questionID}`];
      const isNA = answer === 'N/A';
      if (question.type === 'releaseProfileFormulaChoice') {
        if (typeof answer === 'string' && answer.startsWith('formula_choice_')) {
          releaseProfileFormulaChoices[question.choiceListID] = answer as ReleaseProfileFormulaChoiceID;
        }
      } else if (question.type === 'number') {
        if (isNA) {
          releaseProfileChemicalNaAnswers[question.questionID] = true;
        } else if (typeof answer === 'number') {
          releaseProfileAnswers[question.questionID] = answer;
        }
      } else if (question.type === 'productUsage') {
        if (typeof answer === 'number' && !archivedProductIds.has(question.productID)) {
          // productEdit blocks changes to usageUnits to prevent this conversion from becoming invalid at a future date:
          if (question.units === 'gallons' && question.specificGravity !== null) {
            productUsage.set(question.productID, {
              totalPoundsUsed: answer * 8.345 * question.specificGravity,
              totalGallonsUsed: answer,
            });
          } else {
            productUsage.set(question.productID, {
              totalPoundsUsed: answer,
              totalGallonsUsed: null,
            });
          }
        }
      } else if (question.type === 'productSolids') {
        usingProductSolids = true;
      } else {
        const unexpected: never = question;
        console.error('calculateEmissions: Unexpected question type:', unexpected);
      }
    }

    let processForColumn = process;

    // In theory these are already provided as part of EmissionsProcessAndProfiles, but if the user updates their
    // answers, the UI doesn't update the process structure, so we need to apply them here:
    if (usingProductSolids) {
      processForColumn = {
        ...processForColumn,
        profiles: processForColumn.profiles.map((profile) => {
          return {
            ...profile,
            products: profile.products.map((product) => {
              return {
                ...product,
                solidsPercentageByWeight:
                  (answersMap[`${column}-${productSolidsQuestionID(product.productID, 'weight')}`] as number) ??
                  product.solidsPercentageByWeight,
                solidsPercentageByVolume:
                  (answersMap[`${column}-${productSolidsQuestionID(product.productID, 'volume')}`] as number) ??
                  product.solidsPercentageByVolume,
              };
            }),
          };
        }),
      };
    }

    // Exclude archived products from the process structure so their chemical contributions are not
    // included in calculations for this period:
    if (archivedProductIds.size > 0) {
      processForColumn = {
        ...processForColumn,
        profiles: processForColumn.profiles.map((profile) => {
          return {
            ...profile,
            products: profile.products.filter((product) => !archivedProductIds.has(product.productID)),
          };
        }),
      };
    }

    return {
      releaseProfileAnswers,
      releaseProfileChemicalNaAnswers,
      releaseProfileFormulaChoices,
      productUsage,
      process: processForColumn,
    };
  }

  const inputsByColumn = new Map<EmissionsPeriod | 'potential', ReturnType<typeof inputsFromColumn>>();
  const inputsFor = (column: EmissionsPeriod | 'potential') => {
    const existing = inputsByColumn.get(column);
    if (existing) return existing;
    const built = inputsFromColumn(column);
    inputsByColumn.set(column, built);
    return built;
  };

  const warnings: Warning[] = [];
  const calculationLines: CalculationLine[] = [];

  const acceptableOutputTypes = unitPeriodToAcceptableOutputTypes(
    { trackingMode, limitsWithReleaseProfiles },
    periodOrig,
  );

  for (const outputType of acceptableOutputTypes) {
    const inputs = inputsFor(emissionsAnswersPeriodForOutput(periodOrig, outputType));

    for (let item of chemicalCalculationsExecute(
      { type: 'emissions', acceptableOutputType: outputType },
      profilesAndVariables,
      inputs.productUsage,
      chemicals,
      new Map(), // TRI category mappings are only relevant to TRI.
      [inputs.process],
      {
        releaseProfileAnswers: inputs.releaseProfileAnswers,
        releaseProfileChemicalNaAnswers: inputs.releaseProfileChemicalNaAnswers,
        releaseProfileFormulaChoices: inputs.releaseProfileFormulaChoices,
        wasteTransfers: {}, // Waste transfers are only relevant to TRI, there is no way to represent them in the emissions tool.
      },
      {}, // Usage mixes are not currently supported, as right now we can only calculate them by using TRI data.
      {}, // Waste streams are only relevant to TRI, there is no way to represent them in the emissions tool.
      questionnaireFindChemicalSpecificQuestions(questions.map((q) => q.question)),
    )) {
      if ('warningID' in item) {
        warnings.push(item);
      } else if ('type' in item && (item.type === 'productSeen' || item.type === 'notApplicable')) {
        // Nothing to do.
      } else {
        if (
          trackingMode === 'rollPteIntoActuals' &&
          item.formulaOutputType === 'potential' &&
          periodOrig !== 'potential'
        ) {
          const basisHours = potentialBasisPeriodHours(item.potentialBasisPeriod);
          const multiplier = periodHours / basisHours;

          item = {
            ...item,
            formulaOutputType: 'actual',
            computationResult: item.computationResult * multiplier,
            convertedResult: item.convertedResult * multiplier,
            // TODO: 9. PTE - formulaWithValues and formulaWithVariables do not reflect the multiplier.
          };
        }

        calculationLines.push(item);

        // NOx and SOx are the only real emissions categories we have. Any other category mappings will need to be defined
        // by the user in our report builder:
        if (item.casOrCategoryOriginal === '10102-43-9' || item.casOrCategoryOriginal === '10102-44-0') {
          calculationLines.push({
            ...item,
            lineID: item.lineID + 0.1,
            casOrCategoryMapped: 'NOx' as CasOrCategory,
          });
        } else if (item.casOrCategoryOriginal === '7446-09-5' || item.casOrCategoryOriginal === '7446-11-9') {
          calculationLines.push({
            ...item,
            lineID: item.lineID + 0.2,
            casOrCategoryMapped: 'SOx' as CasOrCategory,
          });
        }
      }
    }
  }

  return { warnings, calculationLines };
}
